import React, { useEffect, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import './styles/SwordfishTrombone.css';
import UploadForm from './components/UploadForm';
import CoverCard from './components/CoverCard';
import Modal from "./components/Modal";


// Auto-detect mount prefix (Namecheap-safe)
const API_PREFIX = '';

const SwordfishTrombone = () => {
  const [covers, setCovers] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Audio refs
  const audioRefs = useRef({});
  const [progress, setProgress] = useState({});
  const playedThisSession = useRef(new Set());

  const [showApprovalPopup, setShowApprovalPopup] = useState(false);


  /* =========================
     LOAD COVERS
     ========================= */
  const loadCovers = async () => {
    setLoadError('');
    try {
      const res = await fetch(`${API_PREFIX}/api/covers`);
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setCovers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setLoadError('Could not load covers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCovers();
  }, []);
  useEffect(() => {
  document.title = 'SwordfishTrombone - The Tom Waits Tribute Repository';
}, []);


  /* =========================
     UPLOAD
     ========================= */
// inside swordfishtrombone.jsx

const handleUpload = async ({ song, artist, email, description, file }) => {
  setUploading(true);
  try {
    const form = new FormData();
    form.append('song', song);
    form.append('artist', artist);
    form.append('email', email);
    form.append('description', description);
    form.append('audio', file);

    const res = await fetch(`${API_PREFIX}/api/upload`, {
      method: 'POST',
      body: form
    });

    if (!res.ok) throw new Error('Upload failed');

    const newCover = await res.json();
    setCovers(prev => [newCover, ...prev]);
    setShowUpload(false);

    setShowApprovalPopup(true);
    setTimeout(() => setShowApprovalPopup(false), 6000);
  } finally {
    setUploading(false);
  }
};


  /* =========================
     VOTING (FormData ONLY)
     ========================= */
  const handleRate = async (id, rating) => {
    try {
      const form = new FormData();
      form.append('id', id);
      form.append('rating', rating);

      const res = await fetch(`${API_PREFIX}/api/rate`, {
        method: 'POST',
        body: form
      });

      if (!res.ok) throw new Error('Vote failed');

      const updated = await res.json();
      setCovers(prev => prev.map(c => (c.id === id ? updated : c)));
    } catch (e) {
      console.error(e);
      alert('Voting failed.');
    }
  };

  /* =========================
     AUDIO CONTROLS
     ========================= */
  const stopAudio = (id) => {
    const a = audioRefs.current[id];
    if (!a) return;
    a.pause();
    a.currentTime = 0;
    setProgress(p => ({ ...p, [id]: 0 }));
  };
const recordPlay = async (id) => {
  if (playedThisSession.current.has(id)) return;
  playedThisSession.current.add(id);

  try {
    const body = new URLSearchParams();
    body.append('id', String(id));

    const res = await fetch(`${API_PREFIX}/api/play`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
      body
    });

    if (!res.ok) {
      const msg = await res.text();
      throw new Error(`Play not recorded (${res.status}): ${msg}`);
    }

    const updated = await res.json();
    setCovers(prev => prev.map(c => (c.id === id ? updated : c)));
  } catch (e) {
    console.error('Failed to record play:', e);
  }
};


  const togglePlay = (id) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    if (playingId === id) {
      audio.pause();
      setPlayingId(null);
      return;
    }

    if (playingId != null) stopAudio(playingId);

audio
  .play()
  .then(() => {
    setPlayingId(id);
    recordPlay(id);
  })
  .catch(console.error);

  };

  const onTimeUpdate = (id, e) => {
    const a = e.target;
    if (!a.duration) return;
    setProgress(p => ({
      ...p,
      [id]: (a.currentTime / a.duration) * 100
    }));
  };

  const onEnded = (id) => {
    if (playingId === id) setPlayingId(null);
    setProgress(p => ({ ...p, [id]: 0 }));
  };

  /* =========================
     HELPERS
     ========================= */
  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const toNum = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  /* =========================
     FILTER + SORT
     ========================= */
  const filtered = covers
    .filter(c =>
      (c.song || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.artist || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'rating') return toNum(b.rating) - toNum(a.rating);
      if (sortBy === 'votes') return toNum(b.votes) - toNum(a.votes);
      return new Date(b.date) - new Date(a.date);
    });

  return (
    <div className="swordfish-container">
      <header className="header">
        <div className="header-content">
          <div>
            <h1 className="logo-text">SWORDFISH</h1>
            <h1 className="logo-text">TROMBONE</h1>
            <div className="subtitle"> A Tribute to The Music Of Tom Waits </div>
            <div className="description">
                Welcome to the Tom Waits Tribute Repository!<br/>
                Sing like yourself, try to sing like Tom, or do an instrumental!<br />
                As long as it's a cover of a Tom Waits song, it belongs here.<br />
            </div>
          </div>

          <div align="center">
            <button
              className="upload-button"
              onClick={() => setShowUpload(v => !v)}
            >
              <Upload /> SUBMIT COVER
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
      {showApprovalPopup && (
          <div className="modal-overlay" onClick={() => setShowApprovalPopup(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="modal-title">Thanks for submitting!</h3>
              <p className="modal-text">
                Please allow up to <b>24 hours</b> for approval before your cover appears.
              </p>
              <button className="modal-button" onClick={() => setShowApprovalPopup(false)}>
                OK
              </button>
            </div>
          </div>
        )}

        {showUpload && (
          <Modal
            open={showUpload}
            title=""
            onClose={() => setShowUpload(false)}
          >
            <UploadForm onUpload={handleUpload} uploading={uploading} />
          </Modal>
        )}


        <div className="search-section">
          <input
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="date">NEWEST</option>
            <option value="rating">TOP RATED</option>
            <option value="votes">MOST VOTED</option>
          </select>
        </div>

        {loading && <p>Loading...</p>}
        {loadError && <p>{loadError}</p>}

        <div className="covers-list">
          {filtered.map((cover) => (
            <CoverCard
              key={cover.id}
              cover={cover}
              isPlaying={playingId === cover.id}
              progressPct={progress[cover.id] || 0}
              onTogglePlay={togglePlay}
              onRate={handleRate}
              audioRef={(el) => { if (el) audioRefs.current[cover.id] = el; }}
              onTimeUpdate={onTimeUpdate}
              onEnded={onEnded}
              formatDate={formatDate}
              toNum={toNum}
            />
          ))}
        </div>
        <div className="description">Contact: <a href="mailto:admin@swordfishtrombone.com?subject=Swordfishtrombone_Inquiry">admin@swordfishtrombone.com</a></div>
      </main>
    </div>
  );
};

export default SwordfishTrombone;
